
    import Vue from "vue";
    import Component from "vue-class-component";
    import { StateChanger } from "vue-infinite-loading";
    import { Watch } from "vue-property-decorator";
    import ApiClient from "@/utilities/ApiClient";
    import Utils from "@/utilities/Utils";
    import ApiButton from "@/vue/components/ApiButton.vue";
    import PrimaryButton from "@/vue/components/PrimaryButton.vue";
    import BriefingDialogue from "@/vue/components/BriefingDialogue.vue";
    import BriefingCloneDialogue from "@/vue/components/BriefingCloneDialogue.vue";
    import PreviewLinksDialogue from "@/vue/components/PreviewLinksDialogue.vue";
    import ChoosePathwayDialogue from "@/vue/components/ChoosePathwayDialogue.vue";
    import { BriefingSearchParameters } from "@/model/BriefingSearchParameters";
    import { IBriefing } from "@/model/Briefing";
    import { ILookupItem } from "@/model/LookupItem";
    import { ISiteLookup } from "@/model/Site";
    import { previewVisitID, UserRole } from "@/model/Enums";
import { IClientLookup } from "@/model/Client";

    interface ISearchResponse {
        list: Array<IBriefing>;
        count: number;
    }

    @Component({
        components: { ApiButton, PrimaryButton, BriefingDialogue, BriefingCloneDialogue, PreviewLinksDialogue, ChoosePathwayDialogue }
    })
    export default class Briefings extends Vue {

        async mounted(): Promise<void> {
            await Promise.all([
                (async () => { this.clientList = await ApiClient.get(Utils.addClientQueryParameters("/api/client/lookups")); })(),
                (async () => { this.siteList = await ApiClient.get(Utils.addClientQueryParameters("/api/site/lookups")); })()
            ]);

            // remove columns the user shouldn't see
            if(!this.canFilterClient) {
                this.briefingHeaders = this.briefingHeaders.filter(h => h.value != "clientName")
            }
            if(!this.canFilterSite) {
                this.briefingHeaders = this.briefingHeaders.filter(h => h.value != "siteName")
            }
        }

        get isAdmin(): boolean { return this.$store.state.signedInUser?.role == UserRole.Admin; }
        get canFilterClient(): boolean { return this.$store.state.signedInUser?.canFilterClient === true; }
        get canFilterSite(): boolean { return this.$store.state.signedInUser?.canFilterSite === true; }

        clientList: Array<IClientLookup> = [];
        get clientOptions(): Array<IClientLookup> { return Utils.clientOptionsFilter(this.clientList); }
        siteList: Array<ISiteLookup> = [];
        get siteOptions(): Array<ISiteLookup> { return Utils.siteOptionsFilter(this.siteList, this.searchParameters.clientID); }

        briefingHeaders = [
            { text: "Active?", value: "isActive", sortable: false, width: "1%"},
            { text: "Client", value: "clientName", sortable: false},
            { text: "Site", value: "siteName", sortable: false},
            { text: "Briefing", value: "name", sortable: false },
            { text: "", value: "options", sortable: false, width: "200px", align: "end", }
        ];

        infiniteId: number = + new Date();
        searchParameters = new BriefingSearchParameters();
        resultCount: string = "-";
        briefingList: Array<IBriefing> = [];

        //
        // -- Watchers
        //

        private debouncedRefreshSearch = Utils.debounce(this.refreshSearch, 200);

        @Watch("searchParameters.searchText")
        private onSearchTextchanged(): void {
            this.debouncedRefreshSearch();
        }

        @Watch("searchParameters.clientID")
        private onClientIdchanged(): void {
            this.debouncedRefreshSearch();
        }

        @Watch("searchParameters.siteID")
        private onSiteIdchanged(): void {
            this.debouncedRefreshSearch();
        }

        //
        // -- methods
        //

        getLinks(briefing: IBriefing): void {
            const dialog: PreviewLinksDialogue = this.$refs.previewLinksDialogue as PreviewLinksDialogue;
            dialog.show(briefing.id);
        }

        confirmClone(briefing: IBriefing): void {
            const dialog: BriefingCloneDialogue = this.$refs.briefingCloneDialogue as BriefingCloneDialogue;
            dialog.show(briefing);
        }

        choosePathwayForPrint(briefing: IBriefing): void {
            const dialog: ChoosePathwayDialogue = this.$refs.choosePathwayForPrintDialogue as ChoosePathwayDialogue;
            dialog.dialogueSubTitle = briefing.name;
            dialog.show(briefing);
        }

        onPathwaySelectedForPrint(pathwayID: string): void {
            const url = `/briefingPrint/${previewVisitID}?previewPathwayID=${pathwayID}`;
            const printWindow = window.open(url);
            if(printWindow == null) {
                alert("Failed to open pop-up print window.  Do you have a pop-up blocker?");
                return;
            }
            //Close window once print is finished
            printWindow.onafterprint = () =>{
                printWindow.close();
            };
        }

        onPathwaySelectedForPreview(pathwayID: string): void {
            const url = `/briefing/${previewVisitID}?previewPathwayID=${pathwayID}`;
            const win = window.open(url, "pathway-preview");
            win?.focus();

            // this.$router.push({
            //     name: "Briefing",
            //     params: { visitID: previewVisitID },
            //     query: { previewPathwayID: pathwayID }
            // });
        }

        editNew(): void {
            const dialog: BriefingDialogue = this.$refs.briefingDialogue as BriefingDialogue;
            dialog.add(this.searchParameters.siteID); // default to site ID we're filtering on
        }

        onBriefingSaved(briefing: IBriefing): void {
            this.$router.push({ name: "AdminBriefing", params: { briefingID: briefing.id } });
        }

        onBriefingCloned(briefingID: string): void {
            this.$router.push({ name: "AdminBriefing", params: { briefingID: briefingID } });
        }

        edit(briefing: IBriefing): void {
            if(this.isAdmin) {
                this.$router.push({ name: "AdminBriefing", params: { briefingID: briefing.id } });
            }
            else {
                const dialog: ChoosePathwayDialogue = this.$refs.choosePathwayForPreviewDialogue as ChoosePathwayDialogue;
                dialog.dialogueSubTitle = briefing.name;
                dialog.show(briefing);
            }
        }

        refreshSearch(): void {
            this.resultCount = "-";
            this.briefingList = [];
            this.searchParameters.pageNumber = 1;
            this.infiniteId += 1;
        }

        async infiniteLoadingHandler(stateChanger: StateChanger): Promise<void> {
            const response: ISearchResponse  = await ApiClient.post("/Api/Briefing/Search", this.searchParameters);
            if(this.searchParameters.pageNumber === 1) {
                this.resultCount = String(response.count);
            }
            if (response.list.length) {
                this.briefingList.push(...response.list);
                this.searchParameters.pageNumber += 1;
                stateChanger.loaded();
            }
            else {
                stateChanger.complete();
            }
        }
    }
